import { Link } from "wouter";
import { SEOHead, SOFTWARE_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import { SeoPageFooter } from "@/components/seo-page-footer";
import {
  ChevronRight,
  CheckCircle2,
  TrendingUp,
  BarChart3,
  RefreshCw,
  ShieldCheck,
  ArrowRight,
  PoundSterling,
} from "lucide-react";

const PAGE_URL = "https://thepropertool.com/ai-construction-estimating-software";

const SOFTWARE_APP_SCHEMA = {
  ...SOFTWARE_SCHEMA,
  name: "The Proper Tool | AI Construction Estimating Software",
  description:
    "AI construction estimating software with a Pricing Accuracy Index that learns from your accepted quotes. Built for UK builders.",
  url: PAGE_URL,
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: "https://thepropertool.com/",
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "AI Construction Estimating Software",
      item: PAGE_URL,
    },
  ],
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is AI construction estimating software?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "AI construction estimating software uses artificial intelligence to generate itemised, costed construction estimates from project inputs such as drawings, photos, or plain-text descriptions. Purpose-built tools like The Proper Tool apply real UK construction rates across all trade categories, then refine those rates over time based on how a builder actually prices and wins work.",
      },
    },
    {
      "@type": "Question",
      name: "What is the Pricing Accuracy Index?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The Pricing Accuracy Index is a feature in The Proper Tool that tracks which quotes you accept, which you adjust, and by how much. It feeds those signals back into future estimates. Over time it learns your margins, your market, and the rate adjustments you make consistently, so each new estimate starts closer to your final number.",
      },
    },
    {
      "@type": "Question",
      name: "How does AI improve construction cost estimation accuracy?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The Proper Tool improves accuracy three ways: it prices from a 519-item UK construction cost library rather than internet averages; its built-in validation pipeline flags pricing outliers and scope gaps before you send; and its Pricing Accuracy Index learns from your accepted quotes so future estimates reflect the rates you actually charge.",
      },
    },
    {
      "@type": "Question",
      name: "Can AI estimating software handle complex construction projects?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. The Proper Tool reads structural drawings, extracting beam sizes, floor areas, spans, and connection details, as well as site photos and plain-text descriptions. Its AI covers all 31 common residential trade categories from groundworks through to decoration, and its validation engine cross-checks quantities against industry norms before you review.",
      },
    },
    {
      "@type": "Question",
      name: "Is there a free trial for AI construction estimating software?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. The Proper Tool offers a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and access to all features. No credit card is required to start. After the trial, plans start from £29/month for sole traders.",
      },
    },
  ],
};

const FAQS = [
  {
    q: "What is AI construction estimating software?",
    a: "AI construction estimating software uses artificial intelligence to generate itemised, costed construction estimates from project inputs such as drawings, photos, or plain-text descriptions. Purpose-built tools like The Proper Tool apply real UK construction rates across all trade categories, then refine those rates over time based on how a builder actually prices and wins work.",
  },
  {
    q: "What is the Pricing Accuracy Index?",
    a: "The Pricing Accuracy Index is a feature in The Proper Tool that tracks which quotes you accept, which you adjust, and by how much. It feeds those signals back into future estimates. Over time it learns your margins, your market, and the rate adjustments you make consistently, so each new estimate starts closer to your final number.",
  },
  {
    q: "How does AI improve construction cost estimation accuracy?",
    a: "The Proper Tool improves accuracy three ways: it prices from a 519-item UK construction cost library rather than internet averages; its built-in validation pipeline flags pricing outliers and scope gaps before you send; and its Pricing Accuracy Index learns from your accepted quotes so future estimates reflect the rates you actually charge.",
  },
  {
    q: "Can AI estimating software handle complex construction projects?",
    a: "Yes. The Proper Tool reads structural drawings, extracting beam sizes, floor areas, spans, and connection details, as well as site photos and plain-text descriptions. Its AI covers all 31 common residential trade categories from groundworks through to decoration, and its validation engine cross-checks quantities against industry norms before you review.",
  },
  {
    q: "Is there a free trial for AI construction estimating software?",
    a: "Yes. The Proper Tool offers a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and access to all features. No credit card is required to start. After the trial, plans start from £29/month for sole traders.",
  },
];

const HOW_IT_LEARNS = [
  {
    icon: BarChart3,
    title: "Tracks your accepted quotes",
    description:
      "Every time a client accepts a quote, the Pricing Accuracy Index notes the rates and margins that worked, in that trade category, at that scope level.",
  },
  {
    icon: RefreshCw,
    title: "Learns your adjustments",
    description:
      "When you edit an AI estimate before sending: adding margin, adjusting a rate, splitting a line. Those changes become signals that shape the next estimate.",
  },
  {
    icon: TrendingUp,
    title: "Improves estimate by estimate",
    description:
      "Over time, your estimates start from closer to your real numbers. Less correction, less time reviewing, more confidence in what you send.",
  },
];

const RELATED_PAGES = [
  { href: "/quoting-software-for-builders/", label: "Quoting Software for Builders" },
  { href: "/construction-estimating-software/", label: "Construction Estimating Software" },
  { href: "/ai-quoting-software/", label: "AI Quoting Software" },
  { href: "/estimating-software-for-builders/", label: "Estimating Software for Builders" },
  { href: "/auto-quote/", label: "Auto Quote" },
  { href: "/ai-takeoff-software/", label: "AI Takeoff Software" },
  { href: "/quote-from-photo/", label: "Quote from Photo" },
  { href: "/quote-from-drawings/", label: "Quote from Drawings" },
  { href: "/quote-from-description/", label: "Quote from a Description" },
  { href: "/pricing/", label: "Pricing" },
  { href: "/free-trial/", label: "Free Trial" },
];

export default function AiConstructionEstimatingSoftwarePage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="AI Construction Estimating Software | The Proper Tool"
        description="AI construction estimating software with a Pricing Accuracy Index that learns from your accepted quotes. Built for UK builders. Free 14-day trial."
        canonical="/ai-construction-estimating-software"
        schema={[SOFTWARE_APP_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        {/* Hero */}
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center">
              <Link href="/" className="hover:text-foreground transition-colors">
                Home
              </Link>
              <ChevronRight className="h-4 w-4" />
              <span>AI Construction Estimating Software</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              AI Construction Estimating Software that learns how you price
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
              The Proper Tool generates fully-itemised construction estimates from your drawings,
              photos, or a description. It gets more accurate with every quote you accept.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link href="/login">
                  Start free, no card needed
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="/pricing/">See pricing</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              14-day free trial · 5 total quote creations across Auto, Template, and Manual quotes · No credit card required
            </p>
          </div>
        </section>

        {/* What is it */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              What is AI construction estimating software?
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              AI construction estimating software uses artificial intelligence to generate
              itemised, costed construction estimates from project inputs: drawings, photos, or a
              plain-text description. Instead of pricing a job line by line from scratch, you hand
              the work to the AI and review what comes back.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              Generic AI tools pull from the internet and produce rough ballparks. The Proper Tool
              is purpose-built for UK residential construction: its AI applies a 519-item cost
              library spanning 31 trade categories, and its validation engine cross-checks every
              estimate before you see it.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              The result is a review-ready, fully itemised estimate (not a rough number) produced
              in under 10 minutes.
            </p>
          </div>
        </section>

        {/* Pricing Accuracy Index */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="mb-10">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                The Pricing Accuracy Index
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-4">
                Every builder prices differently. Different margins, different markets, different
                ways of working. The Pricing Accuracy Index is the part of The Proper Tool that
                makes AI estimating genuinely personal.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                It tracks which quotes you accept, which you adjust, and by how much. Those signals feed back into future estimates. Over time, your estimates start from
                closer to your real numbers. Less correction. Less time reviewing. More confidence
                in what you send.
              </p>
            </div>
            <div className="grid sm:grid-cols-3 gap-6">
              {HOW_IT_LEARNS.map((step) => (
                <div
                  key={step.title}
                  className="bg-background rounded-xl border border-border p-6"
                >
                  <step.icon className="h-6 w-6 text-primary mb-3" />
                  <h3 className="font-semibold text-base mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Built on real UK rates */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Built on real UK construction rates
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              Before the Pricing Accuracy Index even gets involved, every estimate starts from the
              right baseline. The Proper Tool prices from a 519-item cost library covering 31 trade
              categories, built for UK residential construction, with Low, Typical, and High rate
              bands from day one.
            </p>
            <ul className="space-y-3 mb-8">
              {[
                "519 rated tasks across 31 UK trade categories",
                "Low, Typical, and High rate bands built in",
                "Override any rate with your own figures, saved for future estimates",
                "Regional pricing adjustments available on Starter and Pro",
                "Covers every trade from groundworks to decoration",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Validation */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Built-in validation before you send
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              AI can miss things. The Proper Tool's validation pipeline runs automatically on every
              estimate, flagging pricing outliers, scope gaps, and quantity anomalies before the
              quote reaches your client. You see the flags. You decide what to do.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                {
                  icon: ShieldCheck,
                  label: "Quantity cross-checks against project dimensions",
                },
                {
                  icon: TrendingUp,
                  label: "Rate outlier detection: flags items priced above or below norms",
                },
                {
                  icon: BarChart3,
                  label: "Scope gap warnings: alerts when expected trades are missing",
                },
                {
                  icon: PoundSterling,
                  label: "Per-item cost breakdown: labour, materials, and plant split on every line",
                },
              ].map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex items-start gap-3 bg-background rounded-lg border border-border p-4"
                >
                  <Icon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-muted-foreground">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10">
              From inputs to estimate in under 10 minutes
            </h2>
            <div className="space-y-8">
              {[
                {
                  step: "1",
                  title: "Upload your inputs",
                  body: "Attach architect drawings, structural PDFs, site photos, or just describe the job in plain English. The AI reads whatever you have.",
                },
                {
                  step: "2",
                  title: "AI builds the estimate",
                  body: "The AI identifies trades, calculates quantities, and prices every line from the UK cost library. The validation engine runs automatically and flags anything that looks off.",
                },
                {
                  step: "3",
                  title: "Review, edit, and send",
                  body: "Every line is editable. Add or remove items, override rates, adjust margins. When you're happy, export a branded PDF and send it to the client.",
                },
                {
                  step: "4",
                  title: "The Index learns",
                  body: "When the client accepts, or when you adjust before sending, the Pricing Accuracy Index records the signal. Future estimates for similar jobs start from a more calibrated baseline.",
                },
              ].map(({ step, title, body }) => (
                <div key={step} className="flex gap-5">
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm">
                    {step}
                  </div>
                  <div className="pt-1">
                    <h3 className="font-semibold text-base mb-1">{title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing callout */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="pricing">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Simple pricing, free to try</h2>
              <p className="text-lg text-muted-foreground">
              14-day free trial · 5 total quote creations across Auto, Template, and Manual quotes · No credit card required
              </p>
            </div>
            <div className="grid sm:grid-cols-3 gap-6 mb-8">
              {(
                [
                  {
                    name: "Lite",
                    price: "£29",
                    desc: "For sole traders",
                    features: [
                      "10 AI estimates/mo",
                      "Unlimited template & manual quotes",
                      "Professional PDF export",
                      "Full 519-item cost library",
                    ],
                    highlight: false,
                  },
                  {
                    name: "Starter",
                    price: "£59",
                    desc: "For growing businesses",
                    features: [
                      "25 AI estimates/mo",
                      "Everything in Lite",
                      "Pricing Accuracy Index",
                      "Expert validation pipeline",
                      "Regional pricing",
                    ],
                    highlight: true,
                  },
                  {
                    name: "Pro",
                    price: "£99",
                    desc: "For high-volume builders",
                    features: [
                      "50 AI estimates/mo",
                      "Everything in Starter",
                      "Drawing analysis",
                      "Learned rate adjustments",
                    ],
                    highlight: false,
                  },
                ] as {
                  name: string;
                  price: string;
                  desc: string;
                  features: string[];
                  highlight: boolean;
                }[]
              ).map((plan) => (
                <div
                  key={plan.name}
                  className={`rounded-xl border p-6 flex flex-col ${
                    plan.highlight
                      ? "border-primary bg-primary/5"
                      : "border-border bg-background"
                  }`}
                >
                  <div className="mb-4">
                    <p className="font-bold text-lg">{plan.name}</p>
                    <p className="text-3xl font-bold mt-1">
                      {plan.price}
                      <span className="text-base font-normal text-muted-foreground">/mo + VAT</span>
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">{plan.desc}</p>
                  </div>
                  <ul className="space-y-2 flex-1 mb-6">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    variant={plan.highlight ? "default" : "outline"}
                    asChild
                    className="w-full"
                  >
                    <Link href="/login">Start free trial</Link>
                  </Button>
                </div>
              ))}
            </div>
            <div className="text-center space-x-4 text-sm">
              <Link href="/pricing/" className="text-primary font-medium hover:underline">
                Full pricing details
              </Link>
              <span className="text-muted-foreground">·</span>
              <Link href="/free-trial/" className="text-primary font-medium hover:underline">
                About the free trial
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">
              Frequently asked questions
            </h2>
            <div className="space-y-8">
              {FAQS.map((faq) => (
                <div
                  key={faq.q}
                  className="border-b border-border/60 pb-8 last:border-0 last:pb-0"
                >
                  <h3 className="font-semibold text-lg mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Internal links */}
        <section className="py-12 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Related pages</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {RELATED_PAGES.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex items-start gap-2 text-sm text-primary hover:underline font-medium"
                >
                  <ChevronRight className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Ready to estimate faster, and more accurately?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Start your free 14-day trial. 5 total quote creations across Auto, Template, and Manual quotes included. No credit card required.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start your free trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">
              No credit card required · Cancel any time
            </p>
          </div>
        </section>
      </main>

      <SeoPageFooter />
    </div>
  );
}
